import { useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { X, Building2, Plus } from 'lucide-react';
import { ModalRoot } from '@/components/ui/ModalRoot';
import { Z_MODALS } from '@/lib/zIndex';
import { useGameStore } from '@/state/gameStore';
import { STATION_SPECS } from '@/data/stations';
import { formatEuro } from '@/lib/format';
import { StationImage } from '@/components/game/StationImage';
import type { NpcStation } from '@/state/types';

interface NpcAcquireDialogProps {
  npc: NpcStation | null;
  onClose: () => void;
}

/**
 * Phase 7.2.4 — Acquire dialog for an NPC station. Cost is 70 % of tier-1
 * (computed in npcStations.ts) — sofort einsatzbereit, no build time.
 */
export function NpcAcquireDialog({ npc, onClose }: NpcAcquireDialogProps) {
  const credits = useGameStore((s) => s.credits);
  const mode = useGameStore((s) => s.mode);
  const acquire = useGameStore((s) => s.acquireNpcStation);
  const pushToast = useGameStore((s) => s.pushToast);

  useEffect(() => {
    if (!npc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [npc, onClose]);

  if (!npc) return null;

  const spec = STATION_SPECS[npc.kind];
  const affordable = mode === 'sandbox' || credits >= npc.acquireCost;

  const handle = () => {
    const station = acquire(npc.id);
    if (!station) {
      pushToast({ kind: 'error', text: 'Übernahme fehlgeschlagen.' });
      return;
    }
    pushToast({
      kind: 'success',
      text: `${spec.label} „${station.name}" übernommen — sofort einsatzbereit.`,
    });
    onClose();
  };

  return (
    <AnimatePresence>
      <ModalRoot
        onClose={onClose}
        zIndex={Z_MODALS}
        contentClass="w-full max-w-md overflow-hidden rounded-lg border border-border-strong bg-bg-secondary"
      >
          {/* Phase 12.2.3 — Hero image (Wachen-Bild) am oberen Rand. */}
          <StationImage
            kind={npc.kind}
            className="h-32 w-full object-cover"
            alt={`${spec.label}-Wache`}
            fallback={
              <div
                className="flex h-32 w-full items-center justify-center text-white"
                style={{ background: spec.markerColor }}
              >
                <Building2 className="h-8 w-8 opacity-60" aria-hidden />
              </div>
            }
          />
          <div className="mb-4 flex items-start justify-between p-6 pb-0">
            <div className="flex items-start gap-3">
              <span
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md text-white"
                style={{ background: spec.markerColor }}
              >
                <Building2 className="h-4 w-4" aria-hidden />
              </span>
              <div>
                <p className="text-xs uppercase tracking-widest text-accent-dispatch">
                  Wache übernehmen
                </p>
                <h3 className="font-display text-lg font-semibold">{npc.name}</h3>
                {npc.address && (npc.address.street || npc.address.city) && (
                  <p className="mt-0.5 text-[11px] text-fg-secondary">
                    {[
                      [npc.address.street, npc.address.houseNumber]
                        .filter(Boolean)
                        .join(' '),
                      [npc.address.postcode, npc.address.city]
                        .filter(Boolean)
                        .join(' '),
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                )}
                <p className="mt-0.5 text-xs text-fg-muted">
                  {spec.label} · {npc.source === 'osm' ? 'OSM-Daten' : 'Vorgeschlagener Standort'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Schließen"
              className="text-fg-muted transition hover:text-fg-primary"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>

          <div className="px-6">
            <div className="rounded-md border border-border-subtle bg-bg-elevated/40 p-4 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-fg-muted">Kaufpreis (70 % Bestandsrabatt)</span>
                <span className="font-display text-base font-bold tabular-nums text-accent-emergency">
                  {formatEuro(npc.acquireCost)}
                </span>
              </div>
              <div className="mt-1 flex items-center justify-between text-xs">
                <span className="text-fg-muted">Bauzeit</span>
                <span className="tabular-nums text-status-available">sofort</span>
              </div>
              <div className="mt-1 flex items-center justify-between text-xs">
                <span className="text-fg-muted">Guthaben danach</span>
                <span className="tabular-nums">
                  {mode === 'sandbox' ? '∞' : formatEuro(credits - npc.acquireCost)}
                </span>
              </div>
            </div>

            <p className="mt-4 text-xs text-fg-muted">
              Du übernimmst eine bestehende Liegenschaft an dieser Position. Keine Bauphase,
              sofort startbereit auf Stufe 1. Spätere Ausbauten kosten regulär.
            </p>
          </div>

          <div className="mt-5 flex justify-end gap-2 px-6 pb-6">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border-strong bg-bg-elevated px-4 py-2 text-sm font-semibold transition hover:bg-bg-elevated/70"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={handle}
              disabled={!affordable}
              className={[
                'rounded-md px-4 py-2 text-sm font-semibold text-white transition',
                affordable
                  ? 'bg-accent-dispatch hover:bg-accent-dispatch-hover'
                  : 'cursor-not-allowed bg-bg-elevated text-fg-muted',
              ].join(' ')}
            >
              <Plus className="mr-1 inline h-4 w-4" aria-hidden />
              Übernehmen
            </button>
          </div>
      </ModalRoot>
    </AnimatePresence>
  );
}
